import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, BookOpen, Brush, Calculator, Check, Code2, Crown, ShieldCheck, Star } from "lucide-react";
import axios from "axios";
import ImageCarousel from "../components/ImageCarousel";
import { extraescolares } from "../lib/extraescolares";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const cursosPhotos = [
  { url: "/images/cursos/trabajo-colaborativo.jpg", caption: "Trabajo colaborativo" },
  { url: "/images/cursos/grupos-reducidos.jpg", caption: "Grupos reducidos" },
  { url: "/images/cursos/aprendizaje-activo.jpg", caption: "Aprendizaje activo" },
  { url: "/images/cursos/talleres-creativos.jpg", caption: "Talleres creativos" },
];

const activityIcons = {
  dibujo: Brush,
  "programacion-iniciacion": Code2,
  "programacion-avanzado": Code2,
  "hacking-etico": ShieldCheck,
  "matematicas-iniciacion": Calculator,
  "matematicas-avanzado": Calculator,
  ajedrez: Crown,
  "filosofia-para-ninos": BookOpen,
  "aprender-a-pensar": BookOpen,
  "miercoles-con-jovenes": BookOpen,
  "juegos-enriquecimiento": Crown,
  "talleres-varios": Brush,
};

const activityTurnOptions = {
  "hacking-etico": ["Martes y jueves · 19:30–20:30 · Presencial", "Sábados · 10:00–12:00 · Presencial"],
  dibujo: ["Martes y jueves · 17:30"],
  "programacion-iniciacion": ["Martes y jueves · 18:30", "Online · horario a confirmar"],
  "programacion-avanzado": ["Martes y jueves · 19:30", "Online · horario a confirmar"],
  "matematicas-iniciacion": ["Martes y jueves · 18:30"],
  "matematicas-avanzado": ["Martes y jueves · 19:30"],
  ajedrez: ["Lunes · 17:30 · Perfeccionamiento", "Miércoles · 17:30 · Iniciación"],
  "filosofia-para-ninos": ["Martes y jueves · 17:30"],
  "aprender-a-pensar": ["Martes y jueves · 18:00–19:00", "Martes y jueves · 19:00–20:00"],
  "miercoles-con-jovenes": ["Miércoles · 18:30"],
  "juegos-enriquecimiento": ["Viernes · 18:30–20:00"],
  "talleres-varios": ["Sábados · 10:00–14:00"],
};
const activityBySlug = Object.fromEntries(extraescolares.map((activity) => [activity.slug, activity]));
const activitiesWithTurns = extraescolares.map((activity) => ({ ...activity, turnOptions: activityTurnOptions[activity.slug] || [] }));

export default function CursosEspecializados() {
  const location = useLocation();
  const [form, setForm] = useState({
    nombre_alumno: "", apellidos_alumno: "", fecha_nacimiento: "",
    nombre_progenitor_1: "", apellidos_progenitor_1: "", email_progenitor_1: "", telefono_progenitor_1: "",
    nombre_progenitor_2: "", apellidos_progenitor_2: "", email_progenitor_2: "", telefono_progenitor_2: "",
    actividades: [], turnos: {}, titular_cuenta: "", cuenta_bancaria: "", comentarios: "", consentimiento_inscripcion: false,
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    const areaInteres = location.state?.areaInteres;

    if (areaInteres) {
      const selectedActivity = extraescolares.find((activity) => activity.slug === areaInteres || activity.label === areaInteres);
      const selectedValue = selectedActivity?.slug || areaInteres;
      setStep(3);
      setForm((current) => ({
        ...current,
        actividades: current.actividades.includes(selectedValue) ? current.actividades : [...current.actividades, selectedValue],
      }));
    }

    if (location.hash === "#solicitar-info") {
      window.setTimeout(() => {
        document.getElementById("solicitar-info")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, [location.hash, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setStatus(null);
    try {
      const selectedActivities = form.actividades.reduce((items, slug) => {
        if (activityBySlug[slug]) items.push(activityBySlug[slug]);
        return items;
      }, []);
      const activityNames = selectedActivities.map((activity) => activity.label);
      const selectedTurns = Object.fromEntries(selectedActivities.map((activity) => [activity.label, form.turnos[activity.slug] || "Pendiente de confirmar"]));
      await axios.post(`${BACKEND_URL}/api/inscripcion/cursos`, {
        nombre: form.nombre_alumno,
        apellidos: form.apellidos_alumno,
        email: form.email_progenitor_1 || form.email_progenitor_2,
        telefono: form.telefono_progenitor_1 || form.telefono_progenitor_2,
        nombre_alumno: form.nombre_alumno,
        apellidos_alumno: form.apellidos_alumno,
        fecha_nacimiento: form.fecha_nacimiento,
        nombre_progenitor_1: form.nombre_progenitor_1,
        apellidos_progenitor_1: form.apellidos_progenitor_1,
        email_progenitor_1: form.email_progenitor_1 || null,
        telefono_progenitor_1: form.telefono_progenitor_1,
        nombre_progenitor_2: form.nombre_progenitor_2,
        apellidos_progenitor_2: form.apellidos_progenitor_2,
        email_progenitor_2: form.email_progenitor_2 || null,
        telefono_progenitor_2: form.telefono_progenitor_2,
        actividades: activityNames,
        turnos: selectedTurns,
        area_interes: activityNames.join(", "),
        titular_cuenta: form.titular_cuenta,
        cuenta_bancaria: form.cuenta_bancaria,
        consentimiento_inscripcion: form.consentimiento_inscripcion,
        comentarios: form.comentarios,
      });
      setStatus("success");
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestActivity = () => {
    setStep(3);
    window.setTimeout(() => {
      document.getElementById("solicitar-info")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const updateForm = (changes) => setForm((current) => ({ ...current, ...changes }));
  const toggleActivity = (slug) => {
    const activities = form.actividades.includes(slug)
      ? form.actividades.filter((item) => item !== slug)
      : [...form.actividades, slug];
    const turnos = { ...form.turnos };
    if (!activities.includes(slug)) delete turnos[slug];
    updateForm({ actividades: activities, turnos });
  };
  const canContinue = step === 1
    ? Boolean(form.nombre_alumno && form.apellidos_alumno)
    : step === 2
      ? Boolean(
        [1, 2].some((number) => form[`nombre_progenitor_${number}`] && form[`apellidos_progenitor_${number}`] && form[`email_progenitor_${number}`] && form[`telefono_progenitor_${number}`]),
      )
      : step === 3
        ? form.actividades.length > 0 && form.actividades.every((slug) => form.turnos[slug])
        : Boolean(form.titular_cuenta && form.cuenta_bancaria && form.consentimiento_inscripcion);

  const formats = [
    { title: "Grupos reducidos", desc: "Cada extraescolar se organiza en grupos pequeños para favorecer la atención cercana, la participación activa y un clima de confianza." },
    { title: "Trabajo semanal", desc: "Propuestas pensadas para sostenerse durante el curso escolar, con continuidad, ritmo cuidado y espacio para construir aprendizajes con calma." },
    { title: "Reto real", desc: "Actividades con profundidad, preguntas abiertas y proyectos que permiten ir más allá de la repetición o el entretenimiento superficial." },
    { title: "Acompañamiento especializado", desc: "Cada grupo está guiado por profesionales con experiencia en su área y sensibilidad para acompañar perfiles de alta curiosidad e intensidad." },
  ];

  return (
    <main data-testid="cursos-page" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* HERO */}
      <section
        className="relative pt-24 pb-20 px-5 lg:px-8"
        style={{ background: "#FBF3E3" }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="absolute top-0 -right-20 opacity-[0.06]" width="400" height="400" viewBox="0 0 200 200">
            <polygon points="100,5 195,175 5,175" fill="#C9974A" />
          </svg>
          <svg className="absolute -bottom-20 -left-20 opacity-[0.05]" width="350" height="350" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="#C9974A" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="/images/logos/cursos-dacpac.png"
                  alt="DAC-PAC Extraescolares"
                  className="h-10 w-10 object-contain"
                />
                <div className="section-label" style={{ color: "#C9974A" }}>Extraescolares DAC-PAC</div>
              </div>
              <h1
                style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F", lineHeight: 1.15 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              >
                Extraescolares<br />
                <em style={{ color: "#C9974A" }}>para pensar, crear y profundizar.</em>
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Actividades semanales para niños, niñas y jóvenes con curiosidad intensa, ganas de aprender y necesidad de reto. Grupos reducidos, metodología activa y propuestas con sentido durante el curso escolar.
              </p>
              <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full mb-8" style={{ background: "#FFF8EE", border: "1px solid #EEDDB8" }}>
                <Star size={16} style={{ color: "#C9974A" }} />
                <span className="text-sm font-semibold" style={{ color: "#8B6020" }}>Extraescolares 2026/2027 · Información disponible</span>
              </div>
              <div>
                <a href="#solicitar-info" data-testid="cursos-info-cta" className="btn-primary" style={{ background: "#C9974A" }}>
                  Solicitar información <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="rounded-3xl overflow-hidden shadow-xl"
              style={{ height: "440px" }}
            >
              <img
                src="/images/cursos/hero-cursos.jpg"
                alt="Extraescolares para altas capacidades"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* QUÉ SON */}
      <section className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="section-label mb-5" style={{ color: "#C9974A" }}>¿Qué son?</div>
              <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold mb-6">
                Extraescolares con profundidad real
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Las Extraescolares DAC-PAC son actividades de curso escolar pensadas para niños, niñas y jóvenes que necesitan algo más que una actividad repetitiva: reto, profundidad, conversación y proyectos con sentido.
                </p>
                <p>
                  No son clases de refuerzo ni talleres sueltos. Son espacios semanales de aprendizaje activo donde el ritmo, la participación y la calidad del contenido permiten trabajar con calma, foco y continuidad.
                </p>
                <p>
                  Cada grupo está cuidadosamente diseñado para que cada participante pueda implicarse, compartir intereses y recibir una atención cercana. No es necesario contar con una evaluación de alta capacidad para participar.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4 mb-6">
                <p className="text-sm font-semibold uppercase tracking-widest" style={{ color: "#C9974A" }}>Extraescolares disponibles</p>
                <a href="#calendario-semanal" className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: "#8B6020" }}>
                  Ver todas <ArrowRight size={14} />
                </a>
              </div>
              {extraescolares.map((activity) => {
                const Icon = activityIcons[activity.slug] || Star;
                return (
                  <Link
                    key={activity.slug}
                    to={activity.href}
                    className="flex items-center gap-4 px-5 py-4 bg-[#FBF3E3] border border-[#EEDDB8] hover:bg-[#FFF8EE] transition-colors"
                    style={{ borderRadius: "8px" }}
                  >
                    <span className="w-9 h-9 flex items-center justify-center bg-white border border-[#EEDDB8]" style={{ borderRadius: "8px" }}>
                      <Icon size={17} style={{ color: "#C9974A" }} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-800">{activity.shortLabel}</span>
                      <span className="block text-xs text-slate-500">{activity.level}</span>
                    </span>
                    <ArrowRight size={15} className="flex-shrink-0 text-slate-400" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CALENDARIO */}
      <section id="calendario-semanal" className="py-24 px-5 lg:px-8" style={{ background: "#FFF8EE" }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
            <div>
              <div className="section-label mb-4" style={{ color: "#C9974A" }}>Ver todas</div>
              <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold">
                Calendario semanal
              </h2>
              <p className="text-slate-600 mt-3 max-w-2xl">Consulta de un vistazo las actividades, turnos y talleres abiertos de la semana.</p>
            </div>
            <a href="#solicitar-info" className="btn-primary self-start md:self-auto" style={{ background: "#C9974A" }}>
              Inscribirme <ArrowRight size={16} />
            </a>
          </div>
          <div className="overflow-hidden rounded-3xl border border-[#EEDDB8] bg-white shadow-sm">
            <img
              src="/images/cursos/Calendario Extraescolares-selection.png"
              alt="Calendario semanal de actividades extraescolares DAC-PAC"
              className="w-full h-auto"
            />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 border-t border-[#EEDDB8] bg-[#FFF8EE]">
              <div>
                <p className="font-semibold text-[#0D3A4F]">Hacking ético · Presencial</p>
                <p className="text-sm text-slate-600 mt-1">Martes y jueves de 19:30 a 20:30 (1 hora por sesión), o sábados de 10:00 a 12:00 (sesión de 2 horas).</p>
              </div>
              <Link to="/cursos-especializados/hacking-etico" className="inline-flex items-center gap-2 text-sm font-semibold text-[#8B6020]">
                Ver actividad <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FORMATO */}
      <section className="py-24 px-5 lg:px-8" style={{ background: "#FBF3E3" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="section-label mb-4" style={{ color: "#C9974A" }}>Formato</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold">
              Cómo están diseñadas
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formats.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`cursos-format-${i}`}
                className="p-7 rounded-2xl bg-white border border-[#EEDDB8]"
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center mb-4 text-white text-sm font-bold"
                  style={{ background: "#C9974A" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h4 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-xl font-semibold mb-3">{item.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GALERÍA */}
      <section className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="section-label mb-4" style={{ color: "#C9974A" }}>Así trabajamos</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold">
              Aprendizaje activo en grupos reducidos
            </h2>
          </div>
          <ImageCarousel images={cursosPhotos} height="400px" />
        </div>
      </section>

      {/* DISPONIBILIDAD banner */}
      <section className="py-16 px-5 lg:px-8" style={{ background: "#5C3D11" }}>
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 style={{ fontFamily: "Playfair Display, serif", color: "#FFFFFF" }} className="text-2xl font-semibold mb-2">
              Extraescolares 2026/2027 · Plazas abiertas
            </h3>
            <p className="text-sm" style={{ color: "#E8D5B5" }}>
              Ya puedes consultar actividades, horarios, precios y modalidades. Si echas en falta una propuesta concreta, también puedes sugerírnosla.
            </p>
          </div>
          <a href="#solicitar-info" className="btn-primary flex-shrink-0" style={{ background: "#C9974A" }}>
            Quiero información
          </a>
        </div>
      </section>

      {/* PROPUESTAS */}
      <section className="py-16 px-5 lg:px-8" style={{ background: "#FFF8EE" }}>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center border border-[#EEDDB8] bg-white p-7" style={{ borderRadius: "8px" }}>
          <div>
            <div className="section-label mb-3" style={{ color: "#C9974A" }}>¿Buscas otra actividad?</div>
            <h3 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-2xl font-semibold mb-3">
              Cuéntanos qué extraescolar te gustaría encontrar
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Estamos escuchando propuestas de familias para valorar nuevos grupos: ciencia, escritura, robótica, idiomas, creatividad, tecnología u otras áreas de interés.
            </p>
          </div>
          <button type="button" onClick={handleSuggestActivity} className="btn-primary flex-shrink-0 justify-center" style={{ background: "#C9974A" }}>
            Proponer actividad
          </button>
        </div>
      </section>

      {/* FORMULARIO */}
      <section id="solicitar-info" className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-xl mx-auto text-center">
          <div className="section-label mb-5" style={{ color: "#C9974A" }}>Inscripción</div>
          <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold mb-5">
            Inscríbete en una extraescolar
          </h2>
          <p className="text-slate-500 mb-10">
            Completa los datos del alumno, la familia, las actividades y la cuenta para preparar la inscripción.
          </p>

          {status === "success" ? (
            <div
              data-testid="cursos-success-message"
              className="rounded-2xl p-8 text-center"
              style={{ background: "#FBF3E3", border: "1px solid #EEDDB8" }}
            >
              <Check size={32} className="mx-auto mb-4" style={{ color: "#C9974A" }} />
              <p style={{ fontFamily: "Manrope, sans-serif", color: "#8B6020", fontWeight: 600 }}>
                Hemos recibido la inscripción. En los próximos días contactaremos contigo para formalizarla.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8 text-xs font-semibold uppercase tracking-widest text-slate-400">
                {["Alumno", "Familias", "Actividades", "Pagos"].map((label, index) => (
                  <span key={label} className={step === index + 1 ? "text-[#8B6020]" : ""}>{index + 1}. {label}</span>
                ))}
              </div>
              <form onSubmit={handleSubmit} data-testid="cursos-info-form" className="space-y-5 text-left">
                {step === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold" style={{ color: "#0D3A4F" }}>Datos del alumno o alumna</h3>
                    <p className="text-sm text-slate-500">Indica a quién corresponde la inscripción.</p>
                    <input required aria-label="Nombre del alumno o alumna" placeholder="Nombre del alumno/a *" value={form.nombre_alumno} onChange={(e) => updateForm({ nombre_alumno: e.target.value })} data-testid="cursos-alumno-nombre" className="w-full px-5 py-4 rounded-xl border border-slate-200" />
                    <input required aria-label="Apellidos del alumno o alumna" placeholder="Apellidos del alumno/a *" value={form.apellidos_alumno} onChange={(e) => updateForm({ apellidos_alumno: e.target.value })} data-testid="cursos-alumno-apellidos" className="w-full px-5 py-4 rounded-xl border border-slate-200" />
                    <input type="date" aria-label="Fecha de nacimiento del alumno o alumna" value={form.fecha_nacimiento} onChange={(e) => updateForm({ fecha_nacimiento: e.target.value })} className="w-full px-5 py-4 rounded-xl border border-slate-200" />
                  </div>
                )}
                {step === 2 && (
                  <div className="space-y-5">
                    <h3 className="text-xl font-semibold" style={{ color: "#0D3A4F" }}>Datos de los padres o tutores</h3>
                    <p className="text-sm text-slate-500">Los datos del primer progenitor son opcionales si completas los del segundo, pero necesitamos al menos un correo.</p>
                    {[1, 2].map((number) => (
                      <div key={number} className="space-y-3 p-5 border border-[#EEDDB8] rounded-xl">
                        <p className="font-semibold text-[#8B6020]">Progenitor {number}{number === 1 ? " (opcional si completas el segundo)" : ""}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input aria-label={`Nombre del progenitor ${number}`} placeholder="Nombre" value={form[`nombre_progenitor_${number}`]} onChange={(e) => updateForm({ [`nombre_progenitor_${number}`]: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200" />
                          <input aria-label={`Apellidos del progenitor ${number}`} placeholder="Apellidos" value={form[`apellidos_progenitor_${number}`]} onChange={(e) => updateForm({ [`apellidos_progenitor_${number}`]: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200" />
                          <input type="email" aria-label={`Correo electrónico del progenitor ${number}`} placeholder="Correo electrónico" value={form[`email_progenitor_${number}`]} onChange={(e) => updateForm({ [`email_progenitor_${number}`]: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200" />
                          <input type="tel" aria-label={`Teléfono del progenitor ${number}`} placeholder="Teléfono" value={form[`telefono_progenitor_${number}`]} onChange={(e) => updateForm({ [`telefono_progenitor_${number}`]: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {step === 3 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold" style={{ color: "#0D3A4F" }}>Actividades y turnos</h3>
                    <p className="text-sm text-slate-500">Puedes seleccionar varias actividades. Después elige un turno para cada una.</p>
                    <div className="space-y-3">
                      {(() => {
                        const selectedActivities = new Set(form.actividades);
                        return activitiesWithTurns.map((activity) => {
                        const selected = selectedActivities.has(activity.slug);
                        return (
                          <div key={activity.slug} className={`p-4 border rounded-xl ${selected ? "border-[#C9974A] bg-[#FFF8EE]" : "border-slate-200"}`}>
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input type="checkbox" aria-label={`Seleccionar ${activity.label}`} checked={selected} onChange={() => toggleActivity(activity.slug)} />
                              <span className="font-semibold text-slate-800">{activity.label}</span>
                            </label>
                            {selected && (
                              <select required aria-label={`Turno de ${activity.label}`} value={form.turnos[activity.slug] || ""} onChange={(e) => updateForm({ turnos: { ...form.turnos, [activity.slug]: e.target.value } })} className="mt-3 w-full px-4 py-3 rounded-xl border border-slate-200 text-sm">
                                <option value="">Selecciona turno o modalidad *</option>
                                {activity.turnOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                              </select>
                            )}
                          </div>
                        );
                      });
                      })()}
                    </div>
                  </div>
                )}
                {step === 4 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold" style={{ color: "#0D3A4F" }}>Pagos y confirmación</h3>
                    <p className="text-sm text-slate-500">Necesitamos estos datos para preparar la domiciliación de los recibos mensuales.</p>
                    <input required aria-label="Titular de la cuenta bancaria" placeholder="Titular de la cuenta bancaria *" value={form.titular_cuenta} onChange={(e) => updateForm({ titular_cuenta: e.target.value })} className="w-full px-5 py-4 rounded-xl border border-slate-200" />
                    <input required aria-label="IBAN de la cuenta bancaria" placeholder="IBAN *" value={form.cuenta_bancaria} onChange={(e) => updateForm({ cuenta_bancaria: e.target.value })} className="w-full px-5 py-4 rounded-xl border border-slate-200" />
                    <textarea aria-label="Comentarios" placeholder="Comentarios (opcional)" value={form.comentarios} onChange={(e) => updateForm({ comentarios: e.target.value })} rows={3} className="w-full px-5 py-4 rounded-xl border border-slate-200 resize-none" />
                    <label className="flex gap-3 items-start text-sm text-slate-600"><input type="checkbox" required checked={form.consentimiento_inscripcion} onChange={(e) => updateForm({ consentimiento_inscripcion: e.target.checked })} /> <span>Confirmo que los datos son correctos y acepto que se utilicen para gestionar la inscripción y los pagos.</span></label>
                  </div>
                )}
                {status === "error" && <p className="text-red-500 text-sm">Ha ocurrido un error. Revisa los datos e inténtalo de nuevo.</p>}
                <div className="flex gap-3 pt-3">
                  {step > 1 && <button type="button" onClick={() => setStep(step - 1)} className="btn-outline flex-1 justify-center">Atrás</button>}
                  {step < 4 ? <button type="button" disabled={!canContinue} onClick={() => setStep(step + 1)} className="btn-primary flex-1 justify-center disabled:opacity-50" style={{ background: "#C9974A" }}>Continuar</button> : <button type="submit" disabled={loading || !canContinue} data-testid="cursos-submit-btn" className="btn-primary flex-1 justify-center disabled:opacity-60" style={{ background: "#C9974A" }}>{loading ? "Enviando..." : "Enviar inscripción"}</button>}
                </div>
              </form>
            </>
          )}
          {false && (
            <form onSubmit={handleSubmit} data-testid="legacy-cursos-info-form" className="space-y-4 text-left">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Quiero *</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { value: "informacion", label: "Solicitar información" },
                    { value: "inscripcion", label: "Apuntarme directamente" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setForm({ ...form, tipo_solicitud: option.value })}
                      className={`px-5 py-4 text-sm font-semibold border transition-colors text-left ${
                        form.tipo_solicitud === option.value
                          ? "bg-[#FFF8EE] border-[#C9974A] text-[#8B6020]"
                          : "bg-white border-slate-200 text-slate-700 hover:border-[#EEDDB8]"
                      }`}
                      style={{ borderRadius: "8px" }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Nombre *</label>
                  <input type="text" placeholder="Tu nombre" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="cursos-nombre-input"
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Apellidos *</label>
                  <input type="text" placeholder="Apellidos" required value={form.apellidos} onChange={(e) => setForm({ ...form, apellidos: e.target.value })} data-testid="cursos-apellidos-input"
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Email *</label>
                  <input type="email" placeholder="email@ejemplo.com" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cursos-email-input"
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Teléfono{form.tipo_solicitud === "inscripcion" ? " *" : ""}</label>
                  <input type="tel" placeholder="+34 600 000 000" required={form.tipo_solicitud === "inscripcion"} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="cursos-telefono-input"
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">¿Para quién es?</label>
                  <select value={form.para_quien} onChange={(e) => setForm({ ...form, para_quien: e.target.value })} data-testid="cursos-para-quien"
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm"
                  >
                    <option value="">Seleccionar</option>
                    <option value="Para mi hijo/a">Para mi hijo/a</option>
                    <option value="Para varios hermanos/as">Para varios hermanos/as</option>
                    <option value="Estoy valorando opciones">Estoy valorando opciones</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Edad del participante</label>
                  <input type="date" aria-label="Fecha de nacimiento" value={form.fecha_nacimiento} onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} data-testid="cursos-fecha-nacimiento-input"
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Extraescolar de interés{form.tipo_solicitud === "inscripcion" ? " *" : ""}</label>
                <select value={form.area_interes} required={form.tipo_solicitud === "inscripcion"} onChange={(e) => setForm({ ...form, area_interes: e.target.value })} data-testid="cursos-area-interes"
                  className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm"
                >
                  <option value="">Seleccionar extraescolar</option>
                  {extraescolares.map((activity) => (
                    <option key={activity.slug} value={activity.label}>{activity.label}</option>
                  ))}
                  <option value="Programación online">Programación online</option>
                  <option value="Todas">Me interesan todas</option>
                  <option value="Otra extraescolar">Quiero proponer otra extraescolar</option>
                </select>
              </div>
              {form.area_interes === "Otra extraescolar" && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">¿Qué extraescolar te gustaría?</label>
                  <input
                    type="text"
                    placeholder="Ej: robótica, escritura creativa, ciencia..."
                    required
                    value={form.actividad_sugerida}
                    onChange={(e) => setForm({ ...form, actividad_sugerida: e.target.value })}
                    data-testid="cursos-actividad-sugerida-input"
                    className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm"
                  />
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Modalidad o grupo preferido</label>
                <select value={form.modalidad_preferida} onChange={(e) => setForm({ ...form, modalidad_preferida: e.target.value })} data-testid="cursos-modalidad-preferida"
                  className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm"
                >
                  <option value="">Seleccionar si lo tienes claro</option>
                  <option value="Presencial">Presencial</option>
                  <option value="Online">Online</option>
                  <option value="Programación online">Programación online</option>
                  <option value="Grupo L-X">Grupo L-X</option>
                  <option value="Grupo M-J">Grupo M-J</option>
                  <option value="No lo tengo claro">No lo tengo claro</option>
                </select>
              </div>
              <button
                type="button"
                onClick={() => setForm({
                  ...form,
                  area_interes: form.area_interes || "Programación online",
                  modalidad_preferida: "Programación online",
                })}
                className={`w-full p-5 border text-left transition-colors ${
                  form.modalidad_preferida === "Programación online"
                    ? "bg-[#FFF8EE] border-[#C9974A]"
                    : "bg-white border-[#EEDDB8] hover:bg-[#FFF8EE]"
                }`}
                style={{ borderRadius: "8px" }}
                data-testid="cursos-programacion-online-card"
              >
                <span className="block text-sm font-semibold" style={{ color: "#8B6020" }}>
                  Programación online
                </span>
                <span className="block text-sm text-slate-600 mt-1">
                  Marca esta opción si quieres preguntar específicamente por los grupos online de programación.
                </span>
              </button>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Comentarios (opcional)</label>
                <textarea placeholder="¿Algo que quieras contarnos?" value={form.comentarios} onChange={(e) => setForm({ ...form, comentarios: e.target.value })} data-testid="cursos-comentarios-textarea" rows={3}
                  className="w-full px-5 py-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#C9974A] focus:ring-2 focus:ring-[#C9974A]/10 text-sm resize-none"
                />
              </div>
              {status === "error" && (
                <p className="text-red-500 text-sm">Ha ocurrido un error. Inténtalo de nuevo.</p>
              )}
              <button type="submit" disabled={loading} data-testid="cursos-submit-btn" className="btn-primary w-full justify-center py-4 disabled:opacity-60" style={{ background: "#C9974A" }}>
                {loading ? "Enviando..." : form.tipo_solicitud === "inscripcion" ? "Enviar solicitud de inscripción" : "Solicitar información"}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
